import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { Email, Send } from '@mui/icons-material';
import { merchantAuthAPI } from '../../../services/merchantAPI';
import { AutoCloseAlert } from '../../Common';
import useRouteMeta from '../../../hooks/useRouteMeta';

const ForgotPassword = () => {
  // SEO Meta Tags - Automatically applied from config
  useRouteMeta();
  
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    email: '',
  });

  useEffect(() => {
    const isMobile = window.innerWidth < 600; // xs breakpoint
    
    if (isMobile) {
      document.body.style.backgroundColor = 'white';
    } else {
      document.body.style.backgroundColor = '#f5f5f5'; // or whatever your default background color is
    }

    // Cleanup function to restore original background when component unmounts
    return () => {
      document.body.style.backgroundColor = '#f5f5f5'; // restore default
    };
  }, []);

  const validateEmail = (value) => {
    if (!value || !value.trim()) return 'Email is required';
    // RFC 5322 compliant email regex that properly handles all valid email characters
    // This regex allows all valid special characters including +, -, ., etc.
    // It follows the official email specification more closely
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!emailRegex.test(value.trim())) return 'Please enter a valid email address';
    return '';
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setError('');
    setSuccess('');
    
    // Clear field errors when user types
    setFieldErrors(prev => ({
      ...prev,
      email: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Client-side validation - check for empty fields first
    const emailError = validateEmail(email);
    if (emailError) {
      setFieldErrors(prev => ({
        ...prev,
        email: emailError
      }));
      setLoading(false);
      return;
    }

    // Additional defensive check to ensure email is not empty (handles edge cases)
    if (!email.trim()) {
      setFieldErrors(prev => ({
        ...prev,
        email: 'Email is required'
      }));
      setLoading(false);
      return;
    }

    try {
      const response = await merchantAuthAPI.forgotPassword(email);
      setSuccess(response.data.message || 'If your email exists, a password reset link has been sent.');
    } catch (err) {
      // Show all server errors as toast (general error)
      setError(err.response?.data?.error || 'Failed to send password reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm" data-testid="merchant-forgot-password-page">
      <Box
        sx={{
          marginTop: { xs: 0.5, sm: 8 }, // Further reduced top margin for mobile
          marginBottom: { xs: 1, sm: 0 }, // Reduced bottom margin for mobile
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* Glammify Logo */}
    

        {/* Forgot Password Form */}
        <Paper
          elevation={0}
          sx={{
            padding: { xs: 2, sm: 4 }, // Further reduced padding for mobile
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: '18px', // Same as login page
            width: '100%',
            boxShadow: { xs: 'none', sm: '0 4px 6px rgba(0, 0, 0, 0.1)' }, // No shadow on mobile, shadow on larger screens
            border: { xs: 'none', sm: '1px solid #E0E0E0' }, // No border on mobile, border on larger screens
            '@media (min-width: 414px)': {
              padding: 3, // Larger padding for 414px+ devices
            }
          }}
        >
          <Typography component="h1" variant="h5" sx={{ mb: 3, fontWeight: 500 }}>
            Forgot Password 
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Validation Error Alert */}
          {fieldErrors.email && (
            <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
              Please enter a valid email address.
            </Alert>
          )}

          <AutoCloseAlert
            severity="success"
            message={success}
            show={!!success}
            onClose={() => setSuccess('')}
            autoCloseDelay={4000}
          />

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={handleChange}
              disabled={loading}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: '#6318C6' }} />
                  </InputAdornment>
                ),
                sx: {
                  fontSize: { xs: '14px', sm: '16px' } // Smaller font size for mobile
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#6318C6',
                  },
                  backgroundColor: 'white', // Force white background
                  '&.Mui-filled': {
                    backgroundColor: 'white', // Override filled state background
                  },
                  '&:hover': {
                    backgroundColor: 'white', // Keep white on hover
                  },
                  '& input:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 1000px white inset',
                    WebkitTextFillColor: 'inherit',
                  },
                  '& input:-internal-autofill-selected': {
                    backgroundColor: 'white !important',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#6318C6',
                },
              }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2, 
                py: 1.5,
                background: '#6318C6', // Same solid color as login page
                '&:hover': {
                  background: '#5a4db2',
                },
                '&:disabled': {
                  background: '#ccc',
                  color: '#666'
                }
              }}
              disabled={loading || !email.trim()}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <>
                  <Send sx={{ mr: 1, color: 'white' }} /> Send Reset Link
                </>
              )}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2, fontSize: '0.9rem', fontWeight: 500 }}>
              <Link 
                to="/login" 
                style={{ 
                  textDecoration: 'none', 
                  color: '#6318C6', // Same color as login page
                  fontSize: '14px'
                }}
              >
                Back to Login
              </Link>
            </Box>
          </Box>
        </Paper>

       
      </Box>
    </Container>
  );
};

export default ForgotPassword; 